import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, LifeBuoy, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const getSession = () => {
  let s = localStorage.getItem("ait_support_session");
  if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`); localStorage.setItem("ait_support_session", s); }
  return s;
};
const SUGGESTIONS = ["How do deposits work?", "What plans are available?", "How do I withdraw?", "How does the referral reward work?"];
const USER_SUGGESTIONS = ["What's my balance?", "Is my deposit approved?", "Show my investments", "How do I enable 2FA?"];

export function SupportChat() {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const isAdminArea = loc.pathname.startsWith("/admin");

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND}/api/support/chat/${getSession()}`);
      if (r.ok) setMessages(await r.json());
    } catch {}
    setLoaded(true);
  };
  useEffect(() => { if (open && !loaded) load(); if (open) setTimeout(() => inputRef.current?.focus(), 150); }, [open]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  if (isAdminArea) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
    setBusy(true);
    try {
      const token = localStorage.getItem("ait_token");
      const r = await fetch(`${BACKEND}/api/support/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ session_id: getSession(), message: msg }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Request failed");
      }
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta) setMessages((m) => { const c = [...m]; const last = { ...c[c.length - 1] }; last.content += ev.delta; c[c.length - 1] = last; return c; });
        }
      }
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], streaming: false }; return c; });
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: e.message || "Something went wrong. Please try again or use the Contact page.", error: true }; return c; });
    } finally { setBusy(false); }
  };

  const clear = async () => {
    try { await fetch(`${BACKEND}/api/support/chat/${getSession()}`, { method: "DELETE" }); } catch {}
    setMessages([]);
  };

  const suggestions = user ? USER_SUGGESTIONS : SUGGESTIONS;

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="support-chat-open" aria-label="Open support chat"
          className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 text-slate-950 shadow-[0_0_30px_rgba(0,240,255,0.45)] flex items-center justify-center hover:scale-105 transition-transform">
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#050811]" />
        </button>
      )}
      {open && (
        <div data-testid="support-chat-panel" className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] h-[min(620px,calc(100vh-2.5rem))] rounded-2xl border border-cyan-500/25 bg-[#0A0F1D]/95 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-gradient-to-r from-cyan-500/10 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center overflow-hidden"><img src="/brand/logo-mark.png" alt="" className="w-7 h-auto" /></div>
              <div><p className="text-sm font-semibold text-white">Aria · Support</p><p className="text-[11px] text-emerald-300 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online · answers instantly</p></div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && <button onClick={clear} data-testid="support-chat-clear" title="Clear conversation" className="p-2 rounded-lg text-slate-500 hover:text-red-300 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>}
              <button onClick={() => setOpen(false)} data-testid="support-chat-close" className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="support-chat-messages">
            {messages.length === 0 && (
              <div className="space-y-4">
                <div className="glass rounded-2xl rounded-tl-sm p-3.5 text-sm text-slate-200" data-testid="support-chat-welcome">
                  Hi{user ? ` ${user.full_name.split(" ")[0]}` : ""}! I'm Aria, your AI TECHNOLOGY assistant. Ask me anything about deposits, plans, withdrawals, referrals{user ? " or your account" : ""}.
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="support-chat-suggestion" className="text-xs px-3 py-1.5 rounded-full border border-cyan-500/25 text-cyan-200 hover:bg-cyan-500/10 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.role}`}>
                <div className={`max-w-[85%] text-sm whitespace-pre-wrap leading-relaxed px-3.5 py-2.5 rounded-2xl ${m.role === "user" ? "bg-cyan-500 text-slate-950 rounded-tr-sm" : `glass text-slate-200 rounded-tl-sm ${m.error ? "border-red-500/40" : ""}`}`}>
                  {m.content}{m.streaming && !m.content && <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />}{m.streaming && m.content && <span className="inline-block w-1.5 h-4 bg-cyan-300 ml-0.5 animate-pulse align-middle" />}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="px-4 pb-2 flex items-center justify-between">
            <Link to="/contact" onClick={() => setOpen(false)} data-testid="support-chat-contact" className="text-[11px] text-slate-500 hover:text-cyan-300 inline-flex items-center gap-1"><LifeBuoy className="w-3 h-3" /> Need a human? Contact support</Link>
            <span className="text-[10px] text-slate-600">Answers are informational, not financial advice</span>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/5 flex gap-2">
            <input ref={inputRef} data-testid="support-chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about deposits, plans, referrals…" maxLength={2000}
              className="flex-1 bg-slate-900/60 border border-cyan-500/20 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-cyan-400" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="support-chat-send" className="btn-glow w-11 h-11 rounded-xl text-slate-950 flex items-center justify-center disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
